import {Duration} from 'luxon';
import {createSelector} from 'reselect';

import {computeIsItemExpanded, IsExpandedPredicate} from '../../../helpers/expansionTogglingHelpers';
import {
  CollapsedItemKeyPrefixesEnum,
  defaultAccountDetailsSectionsExpandState,
  defaultContactDetailSectionsExpandState,
} from '../../../models/api/localPreferencesModel';
import {AppState} from '../../appState';

export function getLocalPreferences(state: AppState) {
  return state.data.localPreferences;
}

export function getDismissedChannelErrors(state: AppState) {
  return state.data.localPreferences.dismissedChannelErrors;
}

export function getDismissedIntegrationErrors(state: AppState) {
  return state.data.localPreferences.dismissedIntegrationErrors;
}

export function getDismissedIntegrationInformationBanners(state: AppState) {
  return state.data.localPreferences.dismissedIntegrationInformationBanners;
}

export function getRequestedIntegrations(state: AppState) {
  return state.data.localPreferences.requestedIntegrations;
}

export function getSelectedFilters(state: AppState) {
  return state.data.localPreferences.selectedFilters;
}

export function getSortOptions(state: AppState) {
  return state.data.localPreferences.sortOptions;
}

export function getFlags(state: AppState) {
  return state.data.localPreferences.flags;
}

export function getSizes(state: AppState) {
  return state.data.localPreferences.sizes;
}

export function getEmojiUsage(state: AppState) {
  return state.data.localPreferences.emojiUsage;
}

export function getLastSelectedSideFrameId(state: AppState) {
  return state.data.localPreferences.lastSelectedSideFrameId;
}

export function getSelectedSideFrameId(state: AppState) {
  return state.data.localPreferences.selectedSideFrameId;
}

export function getUnassignedAssignedSwitchCount(state: AppState) {
  return state.data.localPreferences.unassignedAssignedSwitchCount;
}

export function getRuleFromDragOptions(state: AppState) {
  return state.data.localPreferences.ruleFromDragOptions;
}

export function getLastRuleCreated(state: AppState) {
  return state.data.localPreferences.lastRuleCreated;
}

export function getLastSuppressionListsRemoval(state: AppState) {
  return state.data.localPreferences.lastSuppressionListsRemoval;
}

export function getCommentReactionErrors(state: AppState) {
  return state.data.localPreferences.commentReactionErrors;
}

export function getHelpCenterSearchCache(state: AppState) {
  return state.data.localPreferences.helpCenterSearchCache;
}

export function getFileCacheItems(state: AppState) {
  return state.data.localPreferences.fileCacheItems;
}

export function getAnalyticsExportsColumns(state: AppState) {
  return state.data.localPreferences.analyticsExportsColumns;
}

export function getSidepanelMultiViewDetails(state: AppState) {
  return state.data.localPreferences.sidepanelMultiViewDetails;
}

export function getLocalPrefEnabledLegacyApplicationIds(state: AppState) {
  return state.data.localPreferences.enabledLegacyApplicationIds;
}

export function getLocalPrefEnabledIntegrationsIds(state: AppState) {
  return state.data.localPreferences.enabledIntegrationIds;
}

/*
 * Collapsed states.
 */

function getCollapsedItems(state: AppState) {
  return state.data.localPreferences.collapsedItems;
}

export const getFindIsExpanded = createSelector(
  getCollapsedItems,
  (collapsedItems): IsExpandedPredicate =>
    (key, defaultValue) =>
      computeIsItemExpanded(collapsedItems, key, defaultValue),
);

export function makeGetFindIsExpanded(key: string, defaultValue: boolean) {
  return createSelector([getCollapsedItems], (collapsedItems) =>
    computeIsItemExpanded(collapsedItems, key, defaultValue),
  );
}

export const contactMergeInfoBoxExpandedKey = `${CollapsedItemKeyPrefixesEnum.MERGE_CONTACTS_BANNER}infoBox`;
export const contactMergeInfoBoxDefaultExpandedState = true;
export const getIsContactMergeInfoBoxExpanded = createSelector(getFindIsExpanded, (findIsExpanded) =>
  findIsExpanded(contactMergeInfoBoxExpandedKey, contactMergeInfoBoxDefaultExpandedState),
);

/*
 * Teammate interactions.
 */

export function getTeammateInteractions(state: AppState) {
  return state.data.localPreferences.teammateInteractions;
}

/* eslint no-param-reassign: 0 */
export const getTeammateInteractionScoresByTeammateId = createSelector(
  getTeammateInteractions,
  (interactions) => {
    const now = Date.now();
    const scale = Duration.fromObject({days: 30}).valueOf();

    // Find the score for each teammate.
    return interactions.reduce((result, interaction) => {
      // Find how much this interaction counts for.
      const contribution = Math.max(0, (scale - (now - interaction.date)) / scale);

      // Add it to the score for this teammate.
      const score = result[interaction.teammateId] || 0;
      result[interaction.teammateId] = score + contribution;

      return result;
    }, {} as {[T: number]: number | undefined});
  },
);

/*
 * Contact Details.
 */

export function getContactDetailsSectionsExpandState(state: AppState) {
  return (
    state.data.localPreferences.contactDetailsPreferences?.sectionsExpandState ||
    defaultContactDetailSectionsExpandState
  );
}

export function getContactDetailsPreferredTab(state: AppState) {
  return state.data.localPreferences.contactDetailsPreferences?.preferredTab;
}

/*
 * Account Details.
 */

export function getShouldDisplayImportAccountsCSVInfoBanner(state: AppState) {
  return Boolean(state.data.localPreferences.accountDetailsPreferences?.shouldDisplayImportCSVInfoBanner);
}

export function getAccountDetailsSectionsExpandState(state: AppState) {
  return (
    state.data.localPreferences.accountDetailsPreferences?.sectionsExpandState ||
    defaultAccountDetailsSectionsExpandState
  );
}

/*
 * Dismissed settings info boxes.
 */

export const getFindShouldDisplayInfoBox = createSelector(
  getLocalPreferences,
  (localPreferences) => (infoBoxId: string) => !localPreferences.dismissedSettingsInfoBoxes[infoBoxId],
);

/*
 * Dismissed default conferencing banner.
 */

export const getHasInteractedWithConferencingBanner = createSelector(
  getLocalPreferences,
  (localPreferences) => localPreferences.dismissedDefaultConferencingBanner.hasInteracted,
);

/*
 * Accounts Setup Guide Completed Tasks.
 */

export const getAccountsSetupGuideCompletedTasks = createSelector(
  getLocalPreferences,
  (localPreferences) => localPreferences.accountsSetupGuideCompletedTasks,
);
